/**
 * useClipboard — SSR-safe clipboard hook with execCommand fallback.
 * @layer atom
 * @package @plerity/ui-core
 */

import { useCallback, useRef, useState } from "react";
import type { UseClipboardOptions, UseClipboardReturn } from "./Clipboard.types";

/** Fallback for non-secure contexts (no navigator.clipboard). */
function execCommandCopy(text: string): boolean {
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
    document.body.appendChild(el);
    el.select();
    // iOS Safari ignores select() on a readonly textarea; an explicit range is required.
    el.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

export function useClipboard(opts?: UseClipboardOptions): UseClipboardReturn {
  const { resetMs = 2000 } = opts ?? {};
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);

      try {
        // SSR guard
        if (typeof navigator === "undefined") {
          setError(new Error("navigator is not available"));
          return false;
        }

        if (navigator.clipboard?.writeText) {
          try {
            await navigator.clipboard.writeText(text);
          } catch (clipErr) {
            // Older iOS Safari / permission-restricted contexts reject writeText
            // even inside a user gesture while the legacy path still works.
            // Surface the ORIGINAL error if the fallback also fails.
            if (!execCommandCopy(text)) throw clipErr;
          }
        } else {
          // Non-secure context fallback
          const ok = execCommandCopy(text);
          if (!ok) throw new Error("execCommand copy failed");
        }

        setError(null);
        setCopied(true);
        resetTimerRef.current = setTimeout(() => setCopied(false), resetMs);
        return true;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        setCopied(false);
        return false;
      }
    },
    [resetMs]
  );

  return { copy, copied, error };
}
